// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { useContext } from "react";
import { Link as RouterLink, useParams, useRouteMatch } from "react-router-dom";
import { Theme, createStyles, makeStyles } from "@material-ui/core";

import { CSSProperties } from "@material-ui/core/styles/withStyles";
import { CustomerContext } from "components/App/DashboardApp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import { getHeaderLogoRoute } from "./getHeaderLogoRoute";
import getSassStyle from "style/sassStyle";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";

const useStyle = makeStyles((theme: Theme) => {
  const colorPrimary: CSSProperties = theme.overrides?.MuiAppBar
    ?.colorPrimary as CSSProperties;

  const color = colorPrimary?.color || getSassStyle().navbackarrowcolor;

  return createStyles({
    backArrow: {
      color: color,
      "&:hover": {
        color: color,
      },
    },
    headerBug: {
      width: "24px",
      display: "block",
    },
  });
});

export default () => {
  const performTestRoute = ROUTE_DEFINITIONS.PERFORMTEST;
  const match = useRouteMatch(getPath(performTestRoute));
  const { getTestRunStep, getStartStep } = useTestRunConfig();
  const { testRunUID, step, page } = useParams() as {
    testRunUID: string;
    step: string;
    page?: string;
  };
  const classes = useStyle();
  const customContext = useContext(CustomerContext)!;
  const { headerConfig } = customContext;

  if (match) {
    const currentStepDescription = getTestRunStep(step);
    if (currentStepDescription.name !== getStartStep()) {
      if (!currentStepDescription.nav.previous) {
        throw Error(`Step ${step} does not specify a previous link`);
      }
      let backUrl;
      if (page && parseInt(page) > 0) {
        backUrl = getPath(performTestRoute, {
          params: {
            ":testRunUID": testRunUID,
            ":step": step,
            ":page": (parseInt(page) - 1).toString(),
          },
        });
      } else {
        const backStep = currentStepDescription.nav.previous!;
        backUrl = getPath(performTestRoute, {
          params: {
            ":testRunUID": testRunUID,
            ":step": backStep,
          },
        });
      }

      return (
        <Link component={RouterLink} to={backUrl} className={classes.backArrow}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            size="medium"
          >
            <FontAwesomeIcon icon="arrow-left" />
          </IconButton>
        </Link>
      );
    }
  }
  if (headerConfig.headerBug) {
    return (
      <Link component={RouterLink} to={getHeaderLogoRoute(customContext)}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label={headerConfig.headerLogo.label}
          size="medium"
        >
          <img
            src={`/assets/logos/${headerConfig.headerBug}`}
            alt={headerConfig.headerLogo.label}
            className={classes.headerBug}
          />
        </IconButton>
      </Link>
    );
  }

  return null;
};
